// Vista del kine: lista de ejecuciones de sesiones de todos sus pacientes,
// agrupadas por fecha. Sin agenda (ver docs/decisions/sesioneskine-modelo.md
// — Opción 1, voto Cowork + voto Alejandro alineados).
//
// El click en un row navega a /paciente/:id (DetallePaciente del kine) —
// mismo destino que rows de DashboardKine. NO se crea pantalla de detalle
// de sesión propio en esta iteración (ver deuda técnica "Vista kine
// dedicada del detalle de sesión").
//
// Sprint 3 B4b: SessionListGrouped + helpers extraídos a shared
// (src/app/components/sessions/SessionListGrouped.tsx + src/lib/utils.ts)
// para reuso con el tab Sesiones del DetallePaciente.

import { useState } from "react";
import { useNavigate } from "react-router";
import { Clock } from "lucide-react";

import { KineHeader } from "../components/kine/KineHeader";
import { KineSidebar } from "../components/kine/KineSidebar";
import { useKineProfile } from "@/lib/supabase/queries/useKineProfile";
import { useSesionesKine } from "@/lib/supabase/queries/useSesionesKine";
import {
  SessionListGrouped,
  type SessionListItem,
} from "@/app/components/sessions/SessionListGrouped";

export function SesionesKine() {
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { data: kineProfile } = useKineProfile();
  const { data: sesiones, error, loading } = useSesionesKine();

  // Stats derivadas del data del hook (no querys adicionales).
  const completadas = sesiones?.filter((s) => s.estado === "completada") ?? [];
  const totalCompletadas = completadas.length;
  const oneWeekAgoMs = Date.now() - 7 * 86400000;
  const estaSemana = completadas.filter(
    (s) => new Date(s.started_at).getTime() >= oneWeekAgoMs,
  ).length;
  const scores = completadas
    .map((s) => s.score_promedio)
    .filter((v): v is number => v !== null);
  const calidadPromedio =
    scores.length > 0
      ? Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 100)
      : null;

  return (
    <div className="min-h-screen bg-background transition-colors duration-200">
      <KineHeader
        kineNombre={kineProfile?.nombre || "—"}
        onMenuClick={() => setDrawerOpen(true)}
      />

      <div className="flex">
        <KineSidebar
          activeKey="sesiones"
          mobileOpen={drawerOpen}
          onMobileClose={() => setDrawerOpen(false)}
        />

        <div className="flex-1 p-8">
          <div className="max-w-[1400px]">
            <div className="mb-6">
              <h1 className="text-2xl font-bold">Sesiones</h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                Ejecuciones de tus pacientes, ordenadas por fecha
              </p>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-6">
              <StatCard
                label="Esta semana"
                value={estaSemana.toString()}
                desc="Sesiones completadas"
                colorClass="text-primary"
              />
              <StatCard
                label="Total completadas"
                value={totalCompletadas.toString()}
                desc="Acumulado histórico"
                colorClass="text-primary"
              />
              <StatCard
                label="Calidad promedio"
                value={calidadPromedio !== null ? `${calidadPromedio}%` : "—"}
                desc="Ejecución técnica medida por cámara"
                colorClass="text-primary"
              />
            </div>

            {loading && <LoadingState />}
            {error && <ErrorState message={error} />}
            {!loading && !error && sesiones && sesiones.length === 0 && (
              <EmptyState />
            )}
            {!loading && !error && sesiones && sesiones.length > 0 && (
              <SessionListGrouped
                sesiones={sesiones as SessionListItem[]}
                onItemClick={(s) =>
                  navigate(
                    s.estado === "completada"
                      ? `/sesion-kine/${s.id}`
                      : `/paciente/${s.paciente_id}`,
                  )
                }
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Subcomponents ────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  desc,
  colorClass,
}: {
  label: string;
  value: string;
  desc: string;
  colorClass: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1">
        {label}
      </p>
      <p className={`text-3xl font-bold ${colorClass}`}>{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{desc}</p>
    </div>
  );
}

function LoadingState() {
  return (
    <p className="text-center text-muted-foreground py-12">
      Cargando sesiones…
    </p>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="bg-card rounded-2xl p-6 border border-border text-center">
      <p className="text-orange-600 font-semibold mb-2">
        No pudimos cargar las sesiones
      </p>
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="bg-card rounded-2xl p-8 border border-border text-center">
      <div className="flex items-center justify-center w-16 h-16 bg-muted rounded-full mx-auto mb-4">
        <Clock className="w-8 h-8 text-muted-foreground" strokeWidth={1.5} />
      </div>
      <h2 className="text-lg font-semibold mb-2">
        Tus pacientes todavía no completaron sesiones
      </h2>
      <p className="text-sm text-muted-foreground leading-relaxed">
        Cuando un paciente termine su primera sesión, va a aparecer acá con
        sus reps y calidad.
      </p>
    </div>
  );
}
